import React from 'react';
import { Link } from 'react-router-dom';
import { Link as LinkIcon, FileText, TrendingUp, Shield, DollarSign, Target, BarChart3, Users, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Guides = () => {
  const guides = [
    {
      id: 'comment-parier-en-ligne-france',
      title: 'Comment parier en ligne en France',
      icon: FileText,
      description: 'Guide complet pour débuter dans les paris en ligne en toute sécurité et légalité.',
    },
    {
      id: 'choisir-meilleur-site-paris',
      title: 'Choisir le meilleur site de paris',
      icon: Target,
      description: 'Critères essentiels pour sélectionner la plateforme qui correspond à vos besoins.',
    },
    {
      id: 'comprendre-bonus-bienvenue',
      title: 'Comprendre les bonus de bienvenue',
      icon: DollarSign,
      description: 'Décryptage des offres promotionnelles et comment en tirer le meilleur parti.',
    },
    {
      id: 'strategies-paris-sportifs',
      title: 'Stratégies de paris sportifs',
      icon: TrendingUp,
      description: 'Techniques éprouvées pour améliorer vos chances de gain sur le long terme.',
    },
    {
      id: 'gerer-bankroll-efficacement',
      title: 'Gérer son bankroll efficacement',
      icon: BarChart3,
      description: 'Méthodes pour protéger votre capital et maximiser votre rentabilité.',
    },
    {
      id: 'types-paris-differents',
      title: 'Les différents types de paris',
      icon: Zap,
      description: 'Panorama complet des marchés disponibles et comment les utiliser.',
    },
    {
      id: 'securite-reglementation',
      title: 'Sécurité et réglementation',
      icon: Shield,
      description: 'Tout savoir sur la légalité et la protection de vos données personnelles.',
    },
    {
      id: 'retirer-gains-rapidement',
      title: 'Retirer ses gains rapidement',
      icon: DollarSign,
      description: 'Optimiser vos retraits et comprendre les délais de chaque méthode de paiement.',
    },
    {
      id: 'parier-direct-conseils',
      title: 'Parier en direct : conseils pratiques',
      icon: Users,
      description: 'Maîtriser les paris en temps réel et profiter des meilleures opportunités.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Guides pratiques pour parier en ligne
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Tout ce que vous devez savoir pour parier en ligne en France en toute confiance et optimiser vos chances de succès
          </p>
        </div>

        {/* About Our Guides Section */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12 mb-16 shadow-lg border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Comment nous rédigeons nos guides
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Chez ParisPari Hub, nous comprenons que le monde des paris en ligne peut sembler complexe, 
              surtout lorsque vous débutez. C'est pourquoi nous avons développé une méthodologie rigoureuse 
              pour créer des guides qui vous accompagnent réellement dans votre parcours.
            </p>
            <p>
              Chaque guide que nous publions est le fruit d'un travail approfondi. Notre équipe teste 
              personnellement chaque plateforme, analyse les conditions générales, compare les offres et 
              évalue l'expérience utilisateur sous tous les angles. Nous ne nous contentons pas de compiler 
              des informations trouvées ailleurs : nous vivons l'expérience pour vous.
            </p>
            <p>
              La transparence est au cœur de notre démarche. Nous vous présentons les avantages comme les 
              inconvénients, sans langue de bois. Si une plateforme présente des faiblesses, nous vous le 
              disons clairement. Si un bonus semble attractif mais cache des conditions défavorables, nous 
              vous alertons. Notre objectif n'est pas de vous vendre un service, mais de vous donner les 
              clés pour faire des choix éclairés.
            </p>
            <p>
              Nos guides sont constamment mis à jour. Le secteur des paris en ligne évolue rapidement : 
              nouvelles réglementations, changements de conditions, apparition de fonctionnalités innovantes. 
              Nous suivons ces évolutions de près et actualisons nos contenus régulièrement pour vous garantir 
              des informations toujours pertinentes et à jour.
            </p>
            <p>
              Enfin, nous privilégions un langage clair et accessible. Pas de jargon technique inutile, 
              pas de termes compliqués sans explication. Nous voulons que chaque guide soit compréhensible, 
              que vous soyez débutant ou parieur expérimenté. Chaque concept est expliqué, chaque étape 
              détaillée, chaque conseil justifié.
            </p>
            <p>
              Ces guides sont votre compagnon de route vers une pratique plus sûre, plus intelligente et 
              plus profitable des paris en ligne. Utilisez-les comme référence, consultez-les régulièrement, 
              et n'hésitez pas à revenir vers nous si vous avez des questions. Votre réussite est notre 
              priorité.
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Chart 1 */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Sites testés mensuellement</h3>
            <div className="h-48 flex items-end justify-center space-x-2">
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '60%' }}></div>
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '80%' }}></div>
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '100%' }}></div>
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '70%' }}></div>
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '90%' }}></div>
              <div className="w-8 bg-primary-red rounded-t" style={{ height: '85%' }}></div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">Plus de 50 plateformes analysées</p>
          </div>

          {/* Chart 2 */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Satisfaction des utilisateurs</h3>
            <div className="h-48 flex items-center justify-center">
              <div className="relative w-32 h-32">
                <svg className="transform -rotate-90 w-32 h-32">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#E5E7EB"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#DC2626"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 56 * 0.87} ${2 * Math.PI * 56}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900">87%</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">Utilisateurs satisfaits</p>
          </div>

          {/* Chart 3 */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Guides mis à jour</h3>
            <div className="h-48 flex flex-col justify-center space-y-3">
              <div className="flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-4 mr-3">
                  <div className="bg-primary-red h-4 rounded-full" style={{ width: '95%' }}></div>
                </div>
                <span className="text-sm font-semibold text-gray-900">2025</span>
              </div>
              <div className="flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-4 mr-3">
                  <div className="bg-primary-red h-4 rounded-full" style={{ width: '88%' }}></div>
                </div>
                <span className="text-sm font-semibold text-gray-900">2024</span>
              </div>
              <div className="flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-4 mr-3">
                  <div className="bg-primary-red h-4 rounded-full" style={{ width: '92%' }}></div>
                </div>
                <span className="text-sm font-semibold text-gray-900">2023</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">Mise à jour régulière</p>
          </div>
        </div>

        {/* Guides Grid */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Tous nos guides
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide) => {
              const Icon = guide.icon;
              return (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.id}`}
                  data-discover="true"
                  itemScope
                  itemType="https://schema.org/Article"
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border-2 border-gray-100 hover:border-primary-red group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-red/10 p-3 rounded-lg group-hover:bg-primary-red/20 transition-colors">
                      <Icon className="text-primary-red" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-red transition-colors" itemProp="headline">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3" itemProp="description">
                        {guide.description}
                      </p>
                      <div className="flex items-center text-primary-red font-semibold text-sm">
                        <span>Lire le guide</span>
                        <LinkIcon className="ml-2" size={16} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-red to-primary-red-dark rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Besoin d'aide supplémentaire ?
          </h2>
          <p className="text-xl mb-6 opacity-90">
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-primary-red px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Nous contacter
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
